<template>
  <div class="ce-page">
    <!-- ── Hero header ────────────────────────────────────────── -->
    <div class="ce-hero">
      <div class="ce-hero__top">
        <span class="ce-hero__eyebrow">{{ $t('Cleaning task') || 'Cleaning task' }}</span>
        <!-- Status dropdown pill — always visible & changeable from the
             sticky hero. Replaces the old in-body Status section. -->
        <b-dropdown
            v-if="currentStatus"
            size="sm"
            no-caret
            right
            toggle-class="ce-status-dd-toggle p-0 border-0 shadow-none"
            menu-class="ce-status-dd-menu"
        >
          <template #button-content>
            <span :class="`ce-status-pill ce-status-pill--${currentStatus.variant}`">
              <feather-icon :icon="iconForStatus(request.status)" size="13" class="mr-50"/>
              {{ currentStatus.label }}
              <feather-icon icon="ChevronDownIcon" size="12" class="ml-50"/>
            </span>
          </template>
          <b-dropdown-item
              v-for="opt in statusOptions"
              :key="opt.value"
              :active="request.status === opt.value"
              @click="request.status = opt.value"
          >
            <span :class="`ce-status-dd-row ce-status-dd-row--${opt.variant}`">
              <feather-icon :icon="iconForStatus(opt.value)" size="14" class="mr-50"/>
              {{ opt.label }}
            </span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <h1 class="ce-hero__title">{{ realEstate.fullAddress || msg('Unknown address') }}</h1>
      <div class="ce-hero__meta">
        <span class="ce-hero__chip">
          <feather-icon icon="UserIcon" size="13" class="mr-25"/>
          <a :href="`/real_estate/contracts/rent/${cleaning.contractId}`" target="_blank">
            {{ cleaning.clientName }}
          </a>
        </span>
        <span class="ce-hero__chip">
          <feather-icon icon="ClockIcon" size="13" class="mr-25"/>
          {{ cleaning.requestedCleanTime | moment('DD/MM/YY HH:mm') }}
        </span>
        <span v-if="cleaning.cleanedTime" class="ce-hero__chip ce-hero__chip--success">
          <feather-icon icon="CheckIcon" size="13" class="mr-25"/>
          {{ msg('Cleaned at') }} {{ cleaning.cleanedTime | moment('DD/MM/YY HH:mm') }}
        </span>
        <span v-if="cleaning.hasDamage" class="ce-hero__chip ce-hero__chip--danger">
          <feather-icon icon="AlertTriangleIcon" size="13" class="mr-25"/>
          {{ msg('Damage reported') }}
        </span>
      </div>
    </div>

    <!-- ── Cleaning report ──────────────────────────────────── -->
    <section class="ce-section">
      <header class="ce-section__head">
        <span class="ce-section__icon ce-section__icon--info">
          <feather-icon icon="FileTextIcon" size="16"/>
        </span>
        <div>
          <h4>{{ msg('Cleaning Report') }}</h4>
          <p>{{ msg('Cleaner name and observations') }}</p>
        </div>
      </header>
      <div class="ce-section__body">
        <b-row>
          <b-col md="5">
            <label class="ce-label">
              <feather-icon icon="UserIcon" size="13" class="mr-25"/>{{ msg('Cleaner') }}
            </label>
            <b-form-input v-model="request.cleaner" :placeholder="msg('Enter cleaners name')"/>
          </b-col>
          <b-col md="7">
            <label class="ce-label">
              <feather-icon icon="MessageSquareIcon" size="13" class="mr-25"/>{{ msg('Notes') }}
            </label>
            <b-form-textarea v-model="request.notes" rows="3" :placeholder="msg('Anything worth mentioning…')"/>
          </b-col>
        </b-row>
      </div>
    </section>

    <!-- ── Photos ─────────────────────────────────────────── -->
    <section class="ce-section" v-if="(isCleanedStatus || isCleaningInProgress)">
      <header class="ce-section__head">
        <span class="ce-section__icon ce-section__icon--gold">
          <feather-icon icon="CameraIcon" size="16"/>
        </span>
        <div>
          <h4>{{ msg('Cleaning Photos') }}</h4>
          <p>
            {{ (cleaning.images || []).length }}
            {{ ((cleaning.images || []).length === 1) ? msg('photo') : msg('photos') }} {{ msg('attached') }}
          </p>
        </div>
      </header>
      <div class="ce-section__body">
        <div v-if="Array.isArray(cleaning.images) && cleaning.images.length" class="ce-photos">
          <div v-for="img in cleaning.images" :key="img" class="ce-photo">
            <b-img
                :src="getUrl(`cleaning/image/${cleaning.id}/${img}`)"
                fluid
                thumbnail
            />
          </div>
        </div>
        <vue-dropzone id="cleaning-dropzone" class="ce-dropzone"
                      :options="dropzoneImagesOptions('task')"
                      :useCustomSlot="true"
                      :duplicateCheck="true"
                      @vdropzone-success="uploadImageSuccess">
          <div class="ce-dropzone__inner">
            <feather-icon icon="UploadCloudIcon" size="22" class="mb-25"/>
            <h5>{{ msg('Drag & drop or click to upload') }}</h5>
            <p>{{ msg('JPG, PNG, max 5 MB') }}</p>
          </div>
        </vue-dropzone>
      </div>
    </section>

    <!-- ── Resources ──────────────────────────────────────── -->
    <section class="ce-section">
      <header class="ce-section__head">
        <span class="ce-section__icon ce-section__icon--warning">
          <feather-icon icon="PackageIcon" size="16"/>
        </span>
        <div>
          <h4>{{ msg('Resources') }}</h4>
          <p>{{ msg('Compare original inventory vs. what is left after the stay.') }}</p>
        </div>
      </header>
      <div class="ce-section__body">
        <div class="ce-resources">
          <h5 class="ce-resources__title">{{ msg('Original Resources') }}</h5>
          <div class="ce-chip-grid" v-if="realEstate.resources && Object.keys(realEstate.resources).length">
            <span
                v-for="(value, key) in realEstate.resources"
                :key="key"
                class="ce-chip"
            >
              <strong>{{ msg(formatResourceKey(key)) }}</strong>
              <span class="ce-chip__sep">·</span>
              <span class="ce-chip__val">{{ value }}</span>
            </span>
          </div>
          <div v-else class="ce-empty-inline">
            <feather-icon icon="InfoIcon" size="13" class="mr-25"/>
            {{ msg('No resources defined for this property.') }}
          </div>
        </div>

        <div class="ce-divider"></div>

        <div class="ce-resources">
          <h5 class="ce-resources__title">{{ msg('Resources after the stay') }}</h5>
          <cleaning-resources v-model="missingResources"/>
        </div>
      </div>
    </section>

    <!-- ── Damage ─────────────────────────────────────────── -->
    <section class="ce-section" :class="{ 'ce-section--danger': hasDamage }">
      <header class="ce-section__head">
        <span class="ce-section__icon" :class="hasDamage ? 'ce-section__icon--danger' : 'ce-section__icon--muted'">
          <feather-icon icon="AlertTriangleIcon" size="16"/>
        </span>
        <div>
          <h4>{{ msg('Damage Report') }}</h4>
          <p>{{ hasDamage ? msg('Damage reported — describe and add photos') : msg('No damage to report') }}</p>
        </div>
        <div class="ce-section__actions">
          <div class="ce-toggle-group">
            <button
                type="button"
                class="ce-toggle ce-toggle--no"
                :class="{ active: hasDamageGroup === 'N' }"
                :disabled="cleaning.cleaned === true || resourceMissing"
                @click="hasDamageGroup = 'N'"
            >
              <feather-icon icon="CheckIcon" size="13" class="mr-25"/>
              {{ msg('No damage') }}
            </button>
            <button
                type="button"
                class="ce-toggle ce-toggle--yes"
                :class="{ active: hasDamageGroup === 'Y' }"
                @click="hasDamageGroup = 'Y'"
            >
              <feather-icon icon="AlertTriangleIcon" size="13" class="mr-25"/>
              {{ msg('Damaged') }}
            </button>
          </div>
        </div>
      </header>
      <transition name="ce-fade">
        <div v-if="hasDamage" class="ce-section__body">
          <label class="ce-label">{{ msg('Damage description') }}</label>
          <b-form-textarea v-model="request.damageDescription" rows="3"
                           :placeholder="msg('What was damaged? Where? How bad?')"/>

          <div class="ce-photos mt-1" v-if="Array.isArray(damages.images) && damages.images.length">
            <div v-for="img in damages.images" :key="img" class="ce-photo">
              <b-img
                  :src="getUrl(`damage/image/${damages.id}/${img}`)"
                  fluid
                  thumbnail
              />
            </div>
          </div>

          <vue-dropzone id="damage-dropzone" class="ce-dropzone ce-dropzone--danger mt-1"
                        :options="dropzoneImagesOptions('damage')"
                        :useCustomSlot="true"
                        :duplicateCheck="true"
                        @vdropzone-success="uploadImageSuccess">
            <div class="ce-dropzone__inner">
              <feather-icon icon="UploadCloudIcon" size="22" class="mb-25"/>
              <h5>{{ msg('Drag & drop damage photos here') }}</h5>
              <p>{{ msg('Show the damage clearly — JPG, PNG, max 5 MB') }}</p>
            </div>
          </vue-dropzone>
        </div>
      </transition>
    </section>

    <!-- ── Floating action bar ────────────────────────────── -->
    <div class="ce-actions">
      <b-button variant="outline-secondary" @click="closeWindow">
        <feather-icon icon="XIcon" size="14" class="mr-25"/>{{ msg('cancel') }}
      </b-button>
      <b-button variant="primary" :disabled="busy" @click="onSave">
        <b-spinner v-if="busy" small class="mr-25"/>
        <feather-icon v-else icon="SaveIcon" size="14" class="mr-25"/>
        {{ msg('Save') }}
      </b-button>
      <b-button variant="success" :disabled="busy" @click="trySaveCleaningTaskAndClose">
        <feather-icon icon="CheckIcon" size="14" class="mr-25"/>
        {{ msg('Save & Close') }}
      </b-button>
    </div>
  </div>
</template>
<script>
import {mapActions} from "vuex";
import vue2Dropzone from "vue2-dropzone";
import RealEstateResources from "@/components/panels/realEstates/realEstateResources.vue";
import CleaningResources from "@/components/panels/realEstates/cleaningResources.vue";

const cleaningRequest = function () {
  return {
    status: null,
    notes: null,
    cleaner: null,
    hasDamage: false,
    damageDescription: null,
    missingResources: null
  }
}

export default {
  name: "cleaningEdit",
  // eslint-disable-next-line vue/no-unused-components
  components: {CleaningResources, vueDropzone: vue2Dropzone,},
  data: () => ({
    busy: false,
    request: cleaningRequest(),
    cleaning: {},
    realEstate: {},
    damages: {},
    id:null,
    missingResources: {},
    resourceMissing: false
  }),
  created() {
    if (this.$route.params.id == null) {
      return this.error = 'Invalid realEstate id'
    }
    this.id = this.$route.params.id;
    this.requestId = this.$route.params.requestId;
    this.refresh();
  }
  ,
  computed: {
    statusEnum() {
      return this.$t('cleaningStatusEnum');
    },
    statusOptions() {
      return Object.keys(this.statusEnum).map(key => ({...this.statusEnum[key], value: key}));
    },
    currentStatus() {
      if (!this.request.status || !this.statusEnum[this.request.status]) return null;
      return this.statusEnum[this.request.status];
    },
    isCleaningInProgress() {
      return this.request.status === 'CLEANING_IN_PROGRESS';
    },
    isCleanedStatus() {
      return this.request.status === 'CLEANED';
    },
    hasDamage() {
      return this.cleaning.hasDamage || this.request.hasDamage;
    },
    hasDamageGroup: {
      get() {
        return this.request.hasDamage? "Y" : "N";
      },
      set(value) {
        this.request.hasDamage = value === "Y";
      }
    },
    dropzoneImagesOptions() {
      let $this = this;
      return function (type) {
        if ($this.cleaning == null || $this.cleaning.id == null) {
          return {}
        }
        return {
          url: window.config.apiUrl + `cleaning/update/${$this.cleaning.id}/upload/img/${type}`,
          headers: {'Authorization': `Bearer ${localStorage.Bearer}`},
          createImageThumbnails: false,
          thumbnailWidth: 50,
          thumbnailHeight: 50,
          addRemoveLinks: true,
          maxFilesize: 5000000,
          acceptedFiles: 'image/*',
        }
      }
    }
  },
  methods: {
    ...mapActions('cleaning', ['saveCleaningTask', 'fetchById']),
    refresh() {
      let $this = this;
      this.fetchById(this.id).then(response => {
        let cleaningtask = response.CleaningTask;
        if (response.Damages != null) {
          cleaningtask.damageDescription = response.Damages.damageDescription;
        }
        $this.show(response.CleaningTask, response.RealEstate, response.Damages);
      });
    },
    formatResourceKey(key) {
      return key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
    },
    iconForStatus(status) {
      switch (status) {
        case 'NEED_CLEANING': return 'ClockIcon';
        case 'CLEANING_IN_PROGRESS': return 'LoaderIcon';
        case 'CLEANED': return 'CheckCircleIcon';
        case 'CANCELLED': return 'XCircleIcon';
        case 'OVERDUE': return 'AlertTriangleIcon';
        default: return 'CircleIcon';
      }
    },
    show(cleaning, realEstate, damages) {
      this.cleaning = {...cleaning};
      this.realEstate = {...realEstate};
      this.request = {...cleaningRequest(), ...cleaning};
      this.damages = damages || {};
    },
    onSave() {
      this.busy = true;
      this.trySaveCleaningTask().finally(() => this.busy = false);
    },
    trySaveCleaningTask() {
      let $this = this;
      this.request.missingResources = this.cleaning.missingResources;
      return this.saveCleaningTask({id: this.cleaning.id, request: this.request}).then(response => {
        $this.cleaning = {...response.CleaningTask};
        $this.missingResources = $this.cleaning.missingResources;
        $this.damages = response.Damages || {};
        $this.$emit('updated', response.CleaningTask);
        $this.$toast.success($this.msg('Cleaning task saved'));
      }).catch((e) => {
        $this.$toast.error($this.msg('Save failed') + (e && e.message ? `: ${e.message}` : ''));
      });
    },
    uploadImageSuccess(file, response) {
      if (response == null || response.objects == null || response.objects['id'] == null)
        return;
      response = response.objects;
      let cleaningtask = response.CleaningTask;
      this.damages = response.Damages || {};
      this.cleaning.hasDamage = cleaningtask.hasDamage;
      this.cleaning.damageId = cleaningtask.damageId;
      this.cleaning.images = cleaningtask.images;
    },
    validateCleaning() {
      let $this = this;
      if (this.cleaning.hasMissingResources) {
        this.resourceMissing = this.cleaning.hasMissingResources != null && this.cleaning.hasMissingResources ? true : false;
        this.request.hasDamage = true;
      }
      return this.trySaveCleaningTask().then(() => $this.isCleanedStatus).catch(() => false);
    },
    trySaveCleaningTaskAndClose() {
      this.busy = true;
      this.validateCleaning().then(() => {
        setTimeout(() => {
          window.close();
        }, 800);
      }).finally(() => this.busy = false);
    },
    closeWindow() {
      window.close();
    }
  }
}
</script>


<style lang="scss">
$ce-radius: 14px;
$ce-radius-lg: 18px;
$ce-border: rgba(60, 60, 70, 0.08);
$ce-border-dark: rgba(255, 255, 255, 0.06);
$ce-primary: #7367f0;
$ce-success: #28c76f;
$ce-warning: #ff9f43;
$ce-danger: #ea5455;
$ce-info: #00cfe8;
$ce-gold: #d4a96a;

.ce-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 16px 16px 100px 16px;
}

/* ── Hero ───────────────────────────────────────────────── */
.ce-hero {
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.08) 0%, rgba(0, 207, 232, 0.05) 100%);
  border: 1px solid $ce-border;
  border-radius: $ce-radius-lg;
  padding: 22px 26px;
  margin-bottom: 16px;
}

.ce-hero__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.ce-hero__eyebrow {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  color: $ce-primary;
  text-transform: uppercase;
}

.ce-hero__status {
  font-size: 0.78rem;
  font-weight: 700;
  padding: 5px 12px;
}

/* ── Status dropdown pill ── */
::v-deep .ce-status-dd-toggle {
  background: transparent !important;
  padding: 0 !important;
}
::v-deep .ce-status-dd-toggle:focus,
::v-deep .ce-status-dd-toggle:active,
::v-deep .ce-status-dd-toggle:hover {
  background: transparent !important;
  box-shadow: none !important;
}

.ce-status-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.18);
  }

  &--warning { background: $ce-warning; color: white; }
  &--info { background: $ce-info; color: white; }
  &--success { background: $ce-success; color: white; }
  &--danger { background: $ce-danger; color: white; }
  &--secondary { background: var(--secondary); color: white; }
  &--primary { background: $ce-primary; color: white; }
}

::v-deep .ce-status-dd-menu {
  border-radius: 12px !important;
  border: 1px solid $ce-border !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14) !important;
  padding: 6px !important;
  min-width: 220px !important;

  .dropdown-item {
    border-radius: 8px;
    margin-bottom: 2px;
    padding: 8px 12px !important;
    font-weight: 700;
    font-size: 0.85rem;
    transition: background 0.12s ease;

    &:last-child { margin-bottom: 0; }
    &:hover { background: rgba(115, 103, 240, 0.08); }
    &.active {
      background: rgba(115, 103, 240, 0.14);
      font-weight: 800;
    }
  }
}

.ce-status-dd-row {
  display: inline-flex;
  align-items: center;

  &--warning { color: $ce-warning; }
  &--info { color: $ce-info; }
  &--success { color: $ce-success; }
  &--danger { color: $ce-danger; }
  &--secondary { color: var(--secondary); }
}

.ce-hero__title {
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0 0 10px 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.ce-hero__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ce-hero__chip {
  display: inline-flex;
  align-items: center;
  background: rgba(115, 103, 240, 0.1);
  color: $ce-primary;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 5px 11px;
  border-radius: 999px;

  a { color: inherit; text-decoration: none; }
  a:hover { text-decoration: underline; }

  &--success {
    background: rgba(40, 199, 111, 0.1);
    color: $ce-success;
  }
  &--danger {
    background: rgba(234, 84, 85, 0.1);
    color: $ce-danger;
  }
}

/* ── Section card ───────────────────────────────────────── */
.ce-section {
  background: var(--card-bg, white);
  border: 1px solid $ce-border;
  border-radius: $ce-radius-lg;
  margin-bottom: 14px;
  overflow: hidden;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover { box-shadow: 0 8px 24px rgba(34, 41, 47, 0.05); }

  &--danger {
    border-color: rgba(234, 84, 85, 0.3);
  }
}

.ce-section__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid $ce-border;

  h4 {
    font-size: 0.95rem;
    font-weight: 800;
    margin: 0;
    letter-spacing: -0.01em;
  }

  p {
    font-size: 0.76rem;
    color: var(--secondary);
    margin: 1px 0 0 0;
    font-weight: 500;
  }
}

.ce-section__icon {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--gold { background: rgba(212, 169, 106, 0.15); color: $ce-gold; }
  &--info { background: rgba(115, 103, 240, 0.12); color: $ce-primary; }
  &--warning { background: rgba(255, 159, 67, 0.12); color: $ce-warning; }
  &--danger { background: rgba(234, 84, 85, 0.12); color: $ce-danger; }
  &--muted { background: rgba(0, 0, 0, 0.04); color: var(--secondary); }
}

.ce-section__head > div { flex: 1; min-width: 0; }
.ce-section__actions { flex-shrink: 0; }

.ce-section__body { padding: 18px; }

.ce-label {
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--secondary);
  margin-bottom: 6px;
}

/* ── Status grid ───────────────────────────────────────── */
.ce-status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.ce-status-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border: 1.5px solid $ce-border;
  border-radius: $ce-radius;
  background: transparent;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  color: var(--secondary);

  &:hover {
    transform: translateY(-1px);
  }

  &--warning { color: $ce-warning; &.active { background: $ce-warning; color: white; border-color: $ce-warning; box-shadow: 0 6px 16px rgba(255, 159, 67, 0.32); } }
  &--info { color: $ce-info; &.active { background: $ce-info; color: white; border-color: $ce-info; box-shadow: 0 6px 16px rgba(0, 207, 232, 0.32); } }
  &--success { color: $ce-success; &.active { background: $ce-success; color: white; border-color: $ce-success; box-shadow: 0 6px 16px rgba(40, 199, 111, 0.32); } }
  &--danger { color: $ce-danger; &.active { background: $ce-danger; color: white; border-color: $ce-danger; box-shadow: 0 6px 16px rgba(234, 84, 85, 0.32); } }
  &--secondary { color: var(--secondary); &.active { background: var(--secondary); color: white; border-color: var(--secondary); } }
}

/* ── Damage toggle (in section header) ─────────────────── */
.ce-toggle-group {
  display: inline-flex;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 999px;
  padding: 3px;
}

.ce-toggle {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border: none;
  border-radius: 999px;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  color: var(--secondary);
  transition: all 0.18s ease;

  &:disabled { opacity: 0.4; cursor: not-allowed; }

  &--no.active {
    background: $ce-success;
    color: white;
    box-shadow: 0 4px 12px rgba(40, 199, 111, 0.32);
  }
  &--yes.active {
    background: $ce-danger;
    color: white;
    box-shadow: 0 4px 12px rgba(234, 84, 85, 0.32);
  }
}

/* ── Photos grid ───────────────────────────────────────── */
.ce-photos {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.ce-photo .img-thumbnail {
  border-radius: 12px;
  border: 1px solid $ce-border;
  height: 120px;
  width: 100%;
  object-fit: cover;
  cursor: zoom-in;
  transition: transform 0.18s ease;

  &:hover {
    transform: scale(1.02);
  }
}

/* ── Dropzone ──────────────────────────────────────────── */
.ce-dropzone {
  border: 2px dashed rgba(115, 103, 240, 0.3) !important;
  background: rgba(115, 103, 240, 0.03) !important;
  border-radius: $ce-radius !important;
  padding: 22px !important;
  min-height: 120px !important;
  cursor: pointer !important;
  transition: all 0.18s ease !important;

  &:hover {
    background: rgba(115, 103, 240, 0.07) !important;
    border-color: $ce-primary !important;
  }

  &--danger {
    border-color: rgba(234, 84, 85, 0.3) !important;
    background: rgba(234, 84, 85, 0.03) !important;

    &:hover {
      background: rgba(234, 84, 85, 0.07) !important;
      border-color: $ce-danger !important;
    }

    .ce-dropzone__inner ::v-deep .feather { color: $ce-danger; }
    .ce-dropzone__inner h5 { color: $ce-danger; }
  }

  .dz-message { margin: 0 !important; }
}

.ce-dropzone__inner {
  text-align: center;
  color: $ce-primary;

  h5 {
    margin: 4px 0 2px 0;
    font-weight: 700;
    font-size: 0.9rem;
    color: $ce-primary;
  }

  p {
    margin: 0;
    font-size: 0.72rem;
    color: var(--secondary);
    font-weight: 500;
  }
}

/* ── Resources ─────────────────────────────────────────── */
.ce-resources__title {
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin-bottom: 10px;
}

.ce-chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ce-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 11px;
  background: rgba(115, 103, 240, 0.06);
  border: 1px solid rgba(115, 103, 240, 0.18);
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text);

  strong {
    color: $ce-primary;
    font-weight: 700;
  }
}

.ce-chip__sep { margin: 0 4px; opacity: 0.5; }
.ce-chip__val { font-weight: 700; }

.ce-empty-inline {
  display: flex;
  align-items: center;
  font-size: 0.84rem;
  color: var(--secondary);
  padding: 8px 0;
}

.ce-divider {
  height: 1px;
  background: $ce-border;
  margin: 18px 0;
}

/* ── Action bar ────────────────────────────────────────── */
.ce-actions {
  position: sticky;
  bottom: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 14px;
  background: var(--card-bg, white);
  border: 1px solid $ce-border;
  border-radius: 999px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  width: fit-content;
  margin-left: auto;
}

/* ── Transitions ───────────────────────────────────────── */
.ce-fade-enter-active,
.ce-fade-leave-active {
  transition: all 0.22s ease;
  overflow: hidden;
}

.ce-fade-enter,
.ce-fade-leave-to {
  opacity: 0;
  max-height: 0;
}

.ce-fade-enter-to,
.ce-fade-leave {
  max-height: 1000px;
}

/* ── Dark mode ─────────────────────────────────────────── */
.dark-layout {
  // Hero gets a stronger gradient on dark to stay visible
  .ce-hero {
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.18) 0%, rgba(0, 207, 232, 0.10) 100%);
    border-color: $ce-border-dark;
  }

  .ce-section,
  .ce-actions { border-color: $ce-border-dark; }
  .ce-section { background: #283046; }
  .ce-section--danger { border-color: rgba(234, 84, 85, 0.4); }
  .ce-section__head { border-bottom-color: $ce-border-dark; }
  .ce-divider { background: $ce-border-dark; }

  // Pills, toggles, status buttons
  .ce-toggle-group { background: rgba(255, 255, 255, 0.06); }
  .ce-status-btn {
    border-color: $ce-border-dark;
    background: rgba(255, 255, 255, 0.02);
  }

  // Status dropdown menu on dark
  ::v-deep .ce-status-dd-menu {
    background: #283046 !important;
    border-color: $ce-border-dark !important;

    .dropdown-item {
      color: rgba(255, 255, 255, 0.85);

      &:hover {
        background: rgba(115, 103, 240, 0.16);
        color: white;
      }
      &.active {
        background: rgba(115, 103, 240, 0.22);
      }
    }
  }

  // Sticky action bar
  .ce-actions {
    background: #283046;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
  }

  // Resource chips
  .ce-chip {
    background: rgba(115, 103, 240, 0.14);
    border-color: rgba(115, 103, 240, 0.3);
    color: rgba(255, 255, 255, 0.9);

    strong { color: #b8b1ff; }
  }
  .ce-empty-inline ::v-deep .feather { color: var(--secondary); }
  .ce-empty-inline { color: rgba(255, 255, 255, 0.5); }

  // Headings + paragraph text
  .ce-section__head h4 { color: rgba(255, 255, 255, 0.95); }
  .ce-section__head p { color: rgba(255, 255, 255, 0.55); }
  .ce-resources__title { color: rgba(255, 255, 255, 0.95); }
  .ce-label { color: rgba(255, 255, 255, 0.55); }

  // Photo thumbnails — give them a dark backdrop instead of white
  .ce-photo .img-thumbnail {
    background: rgba(255, 255, 255, 0.04);
    border-color: $ce-border-dark;
  }

  // Dropzones — deeper tints so dashed border stays visible
  .ce-dropzone {
    background: rgba(115, 103, 240, 0.06) !important;
    border-color: rgba(115, 103, 240, 0.4) !important;

    &:hover {
      background: rgba(115, 103, 240, 0.12) !important;
    }
  }
  .ce-dropzone--danger {
    background: rgba(234, 84, 85, 0.06) !important;
    border-color: rgba(234, 84, 85, 0.4) !important;

    &:hover {
      background: rgba(234, 84, 85, 0.12) !important;
    }
  }

  // Hero chips on dark — better contrast
  .ce-hero__chip {
    background: rgba(115, 103, 240, 0.2);
  }
}

/* ── Responsive ────────────────────────────────────────── */
@media (max-width: 700px) {
  .ce-hero { padding: 18px; }
  .ce-hero__title { font-size: 1.3rem; }
  .ce-section__head { flex-wrap: wrap; }
  .ce-section__actions { width: 100%; }
  .ce-actions {
    position: fixed;
    left: 12px;
    right: 12px;
    bottom: 12px;
    margin: 0;
    width: auto;
    border-radius: $ce-radius;
  }
}

/* legacy class hooks kept for original styles */
.cleaning-edit-container .cleaning-dropzone {
  padding: 0;
  min-height: auto;
}
.cleaning-edit-container .vue-form-wizard .wizard-tab-content { padding: 10px 5px; }
</style>
